import React, { useState, useEffect } from 'react';
import { getFirestore, doc, updateDoc, GeoPoint } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Modal from '../ui/Modal/Modal';
import Autocomplete from "react-google-autocomplete";
import './EditShopForm.css';

const EditShopForm = ({ shop, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    titre: shop.titre || '',
    address: shop.address || '',
    phoneNumber: shop.phoneNumber || '',
    email: shop.email || '',
    coordinates: shop.coordinates ? [shop.coordinates.latitude, shop.coordinates.longitude] : [0, 0],
    hours: shop.hours || {},
    specialHours: shop.specialHours || {},
    holidays: shop.holidays || {}
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(shop.imageUrl || null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general'); // ['general', 'hours', 'special', 'holidays']
  const [selectedDays, setSelectedDays] = useState(
    shop.hours ? Object.keys(shop.hours).reduce((acc, day) => ({
      ...acc,
      [day]: shop.hours[day] !== null && shop.hours[day] !== undefined
    }), {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    }) : {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    }
  );

  const [schedule, setSchedule] = useState(() => {
    if (shop.hours) {
      const firstOpenDay = Object.entries(shop.hours).find(([_, hours]) => hours !== null);
      if (firstOpenDay) {
        const [_, hours] = firstOpenDay;
        return {
          continuous: !hours.afternoon,
          morning: hours.morning || { open: '', close: '' },
          afternoon: hours.afternoon || { open: '', close: '' },
          fullDay: hours.morning || { open: '', close: '' }
        };
      }
    }
    return {
      continuous: false,
      morning: { open: '', close: '' },
      afternoon: { open: '', close: '' },
      fullDay: { open: '', close: '' }
    };
  });

  const [specialHours, setSpecialHours] = useState(shop.specialHours || {});
  const [newSpecialDate, setNewSpecialDate] = useState('');
  const [newSpecialSchedule, setNewSpecialSchedule] = useState({
    continuous: false,
    morning: { open: '', close: '' },
    afternoon: { open: '', close: '' },
    fullDay: { open: '', close: '' }
  });

  const [newHoliday, setNewHoliday] = useState({
    date: '',
    description: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const onPlaceSelected = (place) => {
    if (!place || !place.geometry || !place.geometry.location) {
      console.error("Adresse sélectionnée invalide ou sans géométrie.");
      setFormData(prev => ({ ...prev, coordinates: [0, 0], address: '' })); 
      return;
    }
    console.log("Place sélectionnée (Edit):", place);
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setFormData(prev => ({
      ...prev,
      address: place.formatted_address || prev.address, 
      coordinates: [lat, lng] 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!formData.address || (formData.coordinates[0] === 0 && formData.coordinates[1] === 0)) {
      console.warn("Adresse non valide ou non géocodée. La boutique sera modifiée sans coordonnées GPS.");
    }

    try {
      let imageUrl = shop.imageUrl;
      let newCoordinates = formData.coordinates; 

      let geoPoint = null;
      if (Array.isArray(newCoordinates) && newCoordinates.length === 2 && (newCoordinates[0] !== 0 || newCoordinates[1] !== 0)) {
        geoPoint = new GeoPoint(newCoordinates[0], newCoordinates[1]);
      } else if (shop.coordinates) { 
        geoPoint = shop.coordinates;
      }

      if (imageFile) {
        const storage = getStorage();
        const timestamp = Date.now();
        const storageRef = ref(storage, `shops/${timestamp}_${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      const cleanedHours = {};
      Object.entries(selectedDays).forEach(([day, isSelected]) => {
        cleanedHours[day] = isSelected ? formData.hours[day] : null; 
      });

      const db = getFirestore();
      const shopRef = doc(db, 'shops', shop.id);

      await updateDoc(shopRef, {
        ...(({ hours, ...rest }) => rest)(formData),
        hours: cleanedHours, 
        coordinates: geoPoint, 
        imageUrl,
        dateModification: new Date().toISOString()
      });

      onSuccess();
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      alert('Une erreur est survenue lors de la modification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleDayToggle = (day) => {
    setSelectedDays(prev => {
      const newSelectedDays = {
        ...prev,
        [day]: !prev[day]
      };
      
      const updatedHours = { ...formData.hours };
      
      if (!prev[day]) {
        updatedHours[day] = schedule.continuous 
          ? { morning: schedule.fullDay }
          : {
              morning: schedule.morning,
              afternoon: schedule.afternoon
            };
      } else {
        delete updatedHours[day];
      }
      
      setFormData(prevForm => ({
        ...prevForm,
        hours: updatedHours
      }));
      
      return newSelectedDays;
    });
  };

  const handleScheduleChange = (period, type, value) => {
    setSchedule(prev => {
      const newSchedule = {
        ...prev,
        [period]: type === 'continuous' ? value : {
          ...prev[period],
          [type]: value
        }
      };
      
      const updatedHours = { ...formData.hours };
      Object.keys(selectedDays).forEach(day => {
        if (selectedDays[day]) {
          updatedHours[day] = newSchedule.continuous
            ? { morning: newSchedule.fullDay }
            : {
                morning: newSchedule.morning,
                afternoon: newSchedule.afternoon
              };
        }
      });
      
      setFormData(prevForm => ({
        ...prevForm,
        hours: updatedHours
      }));
      
      return newSchedule;
    });
  };

  const handleAddSpecialHours = () => {
    if (!newSpecialDate) return;

    setSpecialHours(prev => ({
      ...prev,
      [newSpecialDate]: newSpecialSchedule.continuous 
        ? { morning: newSpecialSchedule.fullDay }
        : {
            morning: newSpecialSchedule.morning,
            afternoon: newSpecialSchedule.afternoon
          }
    }));

    setFormData(prev => ({
      ...prev,
      specialHours: {
        ...prev.specialHours,
        [newSpecialDate]: newSpecialSchedule.continuous 
          ? { morning: newSpecialSchedule.fullDay }
          : {
              morning: newSpecialSchedule.morning,
              afternoon: newSpecialSchedule.afternoon
            }
      }
    }));

    setNewSpecialDate('');
    setNewSpecialSchedule({
      continuous: false,
      morning: { open: '', close: '' },
      afternoon: { open: '', close: '' },
      fullDay: { open: '', close: '' }
    });
  };

  const handleRemoveSpecialHours = (date) => {
    const updatedSpecialHours = { ...specialHours };
    delete updatedSpecialHours[date];
    setSpecialHours(updatedSpecialHours);
    
    setFormData(prev => {
      const updated = { ...prev };
      delete updated.specialHours[date];
      return updated;
    });
  };

  const handleAddHoliday = () => {
    if (!newHoliday.date) return;

    setFormData(prev => ({
      ...prev,
      holidays: {
        ...prev.holidays,
        [newHoliday.date]: newHoliday.description || 'Fermé'
      }
    }));

    setNewHoliday({
      date: '',
      description: ''
    });
  };

  const handleRemoveHoliday = (date) => {
    setFormData(prev => {
      const updatedHolidays = { ...prev.holidays };
      delete updatedHolidays[date];
      return {
        ...prev,
        holidays: updatedHolidays
      };
    });
  };

  return (
    <Modal
      title="Modifier la boutique"
      onClose={onClose}
      actions={
        <>
          <button 
            type="button" 
            className="edit-shop-cancel-btn"
            onClick={onClose}
            disabled={isLoading}
          >
            Annuler
          </button>
          <button 
            type="submit" 
            className="edit-shop-submit-btn"
            disabled={isLoading}
            form="editShopForm"
          >
            {isLoading ? 'Modification...' : 'Enregistrer les modifications'}
          </button>
        </>
      }
    >
      <div className="edit-shop-container">
        <div className="edit-shop-tabs">
          <button 
            className={`edit-shop-tab-btn ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            Informations générales
          </button>
          <button 
            className={`edit-shop-tab-btn ${activeTab === 'hours' ? 'active' : ''}`}
            onClick={() => setActiveTab('hours')}
          >
            Horaires réguliers
          </button>
          <button 
            className={`edit-shop-tab-btn ${activeTab === 'special' ? 'active' : ''}`}
            onClick={() => setActiveTab('special')}
          >
            Horaires spéciaux
          </button>
          <button 
            className={`edit-shop-tab-btn ${activeTab === 'holidays' ? 'active' : ''}`}
            onClick={() => setActiveTab('holidays')}
          >
            Jours fériés
          </button>
        </div>

        <form id="editShopForm" onSubmit={handleSubmit} className="edit-shop-form">
          <div className="edit-shop-content">
            {activeTab === 'general' && (
              <div className="edit-shop-general">
                <div className="edit-shop-form-group">
                  <label>Image de la boutique</label>
                  <div className="edit-shop-image-upload">
                    {imagePreview && (
                      <div className="edit-shop-image-preview">
                        <img src={imagePreview} alt="Aperçu" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="edit-shop-file-input"
                    />
                  </div>
                </div>

                <div className="edit-shop-form-group">
                  <label>Nom de la boutique</label>
                  <input
                    type="text"
                    name="titre"
                    value={formData.titre}
                    onChange={handleChange}
                    required
                    placeholder="Exemple : L'UNION - HYPE VAP"
                    className="edit-shop-input"
                  />
                </div>

                <div className="edit-shop-form-group">
                  <label>Adresse</label>
                  <Autocomplete
                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                    style={{ width: '100%' }}
                    onPlaceSelected={onPlaceSelected}
                    options={{
                      types: ["address"],
                      componentRestrictions: { country: "fr" },
                      fields: ["formatted_address", "geometry.location"]
                    }}
                    defaultValue={formData.address}
                    placeholder="Commencez à taper l'adresse..."
                    className="address-autocomplete-input edit-shop-input"
                  />
                  {formData.coordinates[0] !== 0 && formData.coordinates[1] !== 0 && (
                    <small className="coordinates-info edit-shop-field-info">
                      Coordonnées GPS trouvées : {formData.coordinates[0].toFixed(6)}, {formData.coordinates[1].toFixed(6)}
                    </small>
                  )}
                </div>

                <div className="edit-shop-form-group">
                  <label>Téléphone</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="05 XX XX XX XX"
                    className="edit-shop-input"
                  />
                </div>

                <div className="edit-shop-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contact@hypevap.fr"
                    className="edit-shop-input"
                  />
                </div>
              </div>
            )}

            {activeTab === 'hours' && (
              <div className="edit-shop-hours">
                <div className="edit-shop-days-selection">
                  {Object.entries({
                    monday: 'Lundi',
                    tuesday: 'Mardi',
                    wednesday: 'Mercredi',
                    thursday: 'Jeudi',
                    friday: 'Vendredi',
                    saturday: 'Samedi',
                    sunday: 'Dimanche'
                  }).map(([day, label]) => (
                    <label key={day} className="edit-shop-day-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedDays[day]}
                        onChange={() => handleDayToggle(day)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>

                {Object.values(selectedDays).some(Boolean) && (
                  <div className="edit-shop-schedule-config">
                    <div className="edit-shop-schedule-type">
                      <label>
                        <input
                          type="checkbox"
                          checked={schedule.continuous}
                          onChange={(e) => handleScheduleChange('continuous', 'continuous', e.target.checked)}
                        />
                        Horaires en continu
                      </label>
                    </div>

                    {!schedule.continuous ? (
                      <>
                        <div className="edit-shop-periode">
                          <span>Matin</span>
                          <input
                            type="time"
                            value={schedule.morning.open}
                            onChange={(e) => handleScheduleChange('morning', 'open', e.target.value)}
                            className="edit-shop-time-input"
                          />
                          <span>à</span>
                          <input
                            type="time"
                            value={schedule.morning.close}
                            onChange={(e) => handleScheduleChange('morning', 'close', e.target.value)}
                            className="edit-shop-time-input"
                          />
                        </div>
                        <div className="edit-shop-periode">
                          <span>Après-midi</span>
                          <input
                            type="time"
                            value={schedule.afternoon.open}
                            onChange={(e) => handleScheduleChange('afternoon', 'open', e.target.value)}
                            className="edit-shop-time-input"
                          />
                          <span>à</span>
                          <input
                            type="time"
                            value={schedule.afternoon.close}
                            onChange={(e) => handleScheduleChange('afternoon', 'close', e.target.value)}
                            className="edit-shop-time-input"
                          />
                        </div>
                      </>
                    ) : (
                      <div className="edit-shop-periode">
                        <span>Journée</span>
                        <input
                          type="time"
                          value={schedule.fullDay.open}
                          onChange={(e) => handleScheduleChange('fullDay', 'open', e.target.value)}
                          className="edit-shop-time-input"
                        />
                        <span>à</span>
                        <input
                          type="time"
                          value={schedule.fullDay.close}
                          onChange={(e) => handleScheduleChange('fullDay', 'close', e.target.value)}
                          className="edit-shop-time-input"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'special' && (
              <div className="edit-shop-special">
                <div className="edit-shop-special-intro">
                  <h3>Horaires spéciaux</h3>
                  <p className="edit-shop-special-description">
                    Cet onglet vous permet de définir des horaires exceptionnels pour des dates spécifiques 
                    (ouverture exceptionnelle, fermeture anticipée, événement particulier...).
                    Ces horaires remplaceront les horaires réguliers pour la date sélectionnée.
                  </p>
                </div>
                
                <div className="edit-shop-special-form">
                  <div className="edit-shop-form-group">
                    <label>
                      <i className="far fa-calendar-alt"></i>
                      {" "}Date de l'exception
                    </label>
                  <input
                    type="date"
                    value={newSpecialDate}
                    onChange={(e) => setNewSpecialDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                      className="edit-shop-input"
                  />
                    <p className="edit-shop-field-info">Sélectionnez la date pour laquelle vous souhaitez définir des horaires différents</p>
                </div>

                  <div className="edit-shop-schedule-config">
                    <div className="edit-shop-schedule-type">
                    <label>
                      <input
                        type="checkbox"
                        checked={newSpecialSchedule.continuous}
                        onChange={(e) => setNewSpecialSchedule(prev => ({
                          ...prev,
                          continuous: e.target.checked
                        }))}
                      />
                        <span>Horaires en continu</span>
                    </label>
                      <p className="edit-shop-field-info">Cochez cette case si la boutique reste ouverte entre midi et 14h</p>
                  </div>

                  {!newSpecialSchedule.continuous ? (
                    <>
                        <div className="edit-shop-periode">
                        <span>Matin</span>
                          <div className="edit-shop-time-group">
                        <input
                          type="time"
                          value={newSpecialSchedule.morning.open}
                          onChange={(e) => setNewSpecialSchedule(prev => ({
                            ...prev,
                            morning: { ...prev.morning, open: e.target.value }
                          }))}
                              className="edit-shop-time-input"
                        />
                            <span className="edit-shop-time-separator">à</span>
                        <input
                          type="time"
                          value={newSpecialSchedule.morning.close}
                          onChange={(e) => setNewSpecialSchedule(prev => ({
                            ...prev,
                            morning: { ...prev.morning, close: e.target.value }
                          }))}
                              className="edit-shop-time-input"
                        />
                      </div>
                        </div>
                        <div className="edit-shop-periode">
                        <span>Après-midi</span>
                          <div className="edit-shop-time-group">
                        <input
                          type="time"
                          value={newSpecialSchedule.afternoon.open}
                          onChange={(e) => setNewSpecialSchedule(prev => ({
                            ...prev,
                            afternoon: { ...prev.afternoon, open: e.target.value }
                          }))}
                              className="edit-shop-time-input"
                        />
                            <span className="edit-shop-time-separator">à</span>
                        <input
                          type="time"
                          value={newSpecialSchedule.afternoon.close}
                          onChange={(e) => setNewSpecialSchedule(prev => ({
                            ...prev,
                            afternoon: { ...prev.afternoon, close: e.target.value }
                          }))}
                              className="edit-shop-time-input"
                        />
                          </div>
                      </div>
                    </>
                  ) : (
                      <div className="edit-shop-periode">
                      <span>Journée</span>
                        <div className="edit-shop-time-group">
                      <input
                        type="time"
                        value={newSpecialSchedule.fullDay.open}
                        onChange={(e) => setNewSpecialSchedule(prev => ({
                          ...prev,
                          fullDay: { ...prev.fullDay, open: e.target.value }
                        }))}
                            className="edit-shop-time-input"
                      />
                          <span className="edit-shop-time-separator">à</span>
                      <input
                        type="time"
                        value={newSpecialSchedule.fullDay.close}
                        onChange={(e) => setNewSpecialSchedule(prev => ({
                          ...prev,
                          fullDay: { ...prev.fullDay, close: e.target.value }
                        }))}
                            className="edit-shop-time-input"
                      />
                        </div>
                    </div>
                  )}

                  <button 
                    type="button"
                      className="edit-shop-add-special-btn"
                    onClick={handleAddSpecialHours}
                      disabled={!newSpecialDate}
                  >
                      <i className="fas fa-plus-circle"></i>
                      Enregistrer ces horaires exceptionnels
                  </button>
                  </div>
                </div>

                {Object.keys(specialHours).length > 0 && (
                  <div className="edit-shop-special-hours-list">
                    <h4>
                      <i className="fas fa-clock"></i>
                      Horaires exceptionnels enregistrés
                    </h4>
                    {Object.entries(specialHours)
                      .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
                      .map(([date, hours]) => (
                        <div key={date} className="edit-shop-special-hours-item">
                          <div className="edit-shop-special-date">
                            <i className="far fa-calendar"></i>
                            {new Date(date).toLocaleDateString('fr-FR', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                        </div>
                          <div className="edit-shop-special-time">
                            <i className="far fa-clock"></i>
                            {hours.morning && !hours.afternoon && 
                              `${hours.morning.open} - ${hours.morning.close}`
                            }
                            {hours.morning && hours.afternoon && 
                              `${hours.morning.open} - ${hours.morning.close}, ${hours.afternoon.open} - ${hours.afternoon.close}`
                            }
                        </div>
                        <button
                          type="button"
                            className="edit-shop-remove-special-btn"
                          onClick={() => handleRemoveSpecialHours(date)}
                            title="Supprimer ces horaires exceptionnels"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'holidays' && (
              <div className="edit-shop-holidays">
                <div className="edit-shop-holidays-intro">
                  <h3>
                    <i className="fas fa-calendar-times"></i>
                    Jours fériés et fermetures exceptionnelles
                  </h3>
                  <p className="edit-shop-holidays-description">
                    Cette section vous permet d'indiquer les jours où votre boutique sera fermée 
                    (jours fériés, congés, fermetures exceptionnelles...). 
                    Ces dates seront affichées comme "Fermé" dans l'application mobile.
                  </p>
                </div>
                
                <div className="edit-shop-holidays-form">
                  <div className="edit-shop-form-group">
                    <label>
                      <i className="far fa-calendar-alt"></i>
                      {" "}Date de fermeture
                    </label>
                  <input
                    type="date"
                    value={newHoliday.date}
                    onChange={(e) => setNewHoliday(prev => ({
                      ...prev,
                      date: e.target.value
                    }))}
                    min={new Date().toISOString().split('T')[0]}
                      className="edit-shop-input"
                  />
                    <p className="edit-shop-field-info">Sélectionnez la date à laquelle la boutique sera fermée</p>
                </div>

                  <div className="edit-shop-form-group">
                    <label>
                      <i className="fas fa-info-circle"></i>
                      {" "}Motif de fermeture
                    </label>
                  <input
                    type="text"
                    value={newHoliday.description}
                    onChange={(e) => setNewHoliday(prev => ({
                      ...prev,
                      description: e.target.value
                    }))}
                      placeholder="Ex: Jour de l'An, Noël, Congés d'été..."
                      className="edit-shop-input"
                  />
                    <p className="edit-shop-field-info">Cette information sera affichée dans l'application mobile (optionnel)</p>
                </div>

                <button 
                  type="button"
                    className="edit-shop-add-holiday-btn"
                  onClick={handleAddHoliday}
                    disabled={!newHoliday.date}
                >
                    <i className="fas fa-plus-circle"></i>
                    Ajouter cette date de fermeture
                </button>
                </div>

                {Object.keys(formData.holidays).length > 0 && (
                  <div className="edit-shop-holidays-list">
                    <h4>
                      <i className="fas fa-list-alt"></i>
                      {" "}Dates de fermeture enregistrées
                    </h4>
                    {Object.entries(formData.holidays)
                      .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
                      .map(([date, description]) => (
                        <div key={date} className="edit-shop-holiday-item">
                          <div className="edit-shop-holiday-date">
                            <i className="far fa-calendar-times"></i>
                            {new Date(date).toLocaleDateString('fr-FR', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </div>
                          <div className="edit-shop-holiday-description">
                            <i className="fas fa-info-circle"></i>
                            {description || "Fermé"}
                          </div>
                          <button
                            type="button"
                            className="edit-shop-remove-holiday-btn"
                            onClick={() => handleRemoveHoliday(date)}
                            title="Supprimer cette date de fermeture"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditShopForm; 